import { Plus, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Kolonne } from '../../../api/reise';

/** En rad mens kunden fyller ut: tekst, tall og valg som tekst, og ja/nei som sann/usann. */
export interface Rad {
  nokkel: number;
  celler: Record<string, string | boolean>;
}

let nesteNokkel = 1;

export function nyRad(celler: Record<string, string | boolean> = {}): Rad {
  return { nokkel: nesteNokkel++, celler };
}

/** Radene fra et svar som er gitt: tall blir tekst i feltet, ja/nei står som det er. */
export function raderFraSvar(verdi: unknown): Rad[] {
  if (!Array.isArray(verdi)) return [];
  return verdi
    .filter((r): r is Record<string, unknown> => typeof r === 'object' && r !== null)
    .map((r) =>
      nyRad(
        Object.fromEntries(
          Object.entries(r)
            .filter(([, v]) => v !== null && v !== undefined)
            .map(([k, v]) => [k, typeof v === 'boolean' ? v : String(v)])
        )
      )
    );
}

/** Feil i en celle: hvilken rad og kolonne. */
export interface Cellefeil {
  rad: number;
  kolonne: string;
}

/**
 * Radene som sendes: tomme celler utelates, tall gjøres om, og helt tomme rader tas bort. Gir feilen for
 * den første cellen med et ugyldig tall.
 */
export function raderTilSvar(kolonner: Kolonne[], rader: Rad[]): Record<string, unknown>[] | Cellefeil {
  const resultat: Record<string, unknown>[] = [];
  for (let indeks = 0; indeks < rader.length; indeks++) {
    const rad = rader[indeks];
    const ut: Record<string, unknown> = {};
    let harInnhold = false;
    for (const k of kolonner) {
      const verdi = rad.celler[k.id];
      if (k.type === 'ja_nei') {
        ut[k.id] = verdi === true;
        harInnhold ||= verdi === true;
        continue;
      }
      const tekst = typeof verdi === 'string' ? verdi.trim() : '';
      if (!tekst) continue;
      harInnhold = true;
      if (k.type === 'tall') {
        const tall = Number(tekst.replace(',', '.'));
        if (Number.isNaN(tall)) return { rad: indeks + 1, kolonne: k.tekst };
        ut[k.id] = tall;
      } else {
        ut[k.id] = tekst;
      }
    }
    if (harInnhold) resultat.push(ut);
  }
  return resultat;
}

interface Props {
  id: string;
  kolonner: Kolonne[];
  rader: Rad[];
  onEndre: (rader: Rad[]) => void;
}

/**
 * Tabellspørsmål: én rad per ting (for eksempel en leveringsmåte), med kolonnene som felt. Radene vises som
 * kort, så de er lette å fylle ut på mobil, og feltene legger seg side om side på bredere skjermer.
 */
export default function Tabellfelt({ id, kolonner, rader, onEndre }: Props) {
  const { t } = useTranslation();

  function endreCelle(nokkel: number, kolonne: string, verdi: string | boolean) {
    onEndre(rader.map((r) => (r.nokkel === nokkel ? { ...r, celler: { ...r.celler, [kolonne]: verdi } } : r)));
  }

  return (
    <div>
      {rader.length === 0 && <p className="mb-3 text-slate-600">{t('reise.tabell.ingen')}</p>}
      <ol className="flex flex-col gap-3">
        {rader.map((rad, indeks) => {
          const nr = indeks + 1;
          return (
            <li key={rad.nokkel}>
              <fieldset className="rounded-lg border border-slate-200 bg-white p-4">
                <legend className="px-1 text-sm font-semibold text-slate-700">{t('reise.tabell.rad', { nr })}</legend>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                  {kolonner.map((k) => (
                    <Celle
                      key={k.id}
                      id={`${id}-r${rad.nokkel}-${k.id}`}
                      kolonne={k}
                      verdi={rad.celler[k.id]}
                      onEndre={(verdi) => endreCelle(rad.nokkel, k.id, verdi)}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => onEndre(rader.filter((r) => r.nokkel !== rad.nokkel))}
                  className="mt-3 inline-flex min-h-[3rem] items-center gap-2 rounded px-2 text-sm text-red-800 hover:bg-red-50"
                >
                  <Trash2 aria-hidden="true" className="h-4 w-4" />
                  {t('reise.tabell.fjern', { nr })}
                </button>
              </fieldset>
            </li>
          );
        })}
      </ol>
      <button type="button" onClick={() => onEndre([...rader, nyRad()])} className="knapp-sekundaer mt-3 inline-flex items-center gap-2">
        <Plus aria-hidden="true" className="h-4 w-4" />
        {t('reise.tabell.leggTil')}
      </button>
    </div>
  );
}

function Celle({ id, kolonne, verdi, onEndre }: { id: string; kolonne: Kolonne; verdi: string | boolean | undefined; onEndre: (v: string | boolean) => void }) {
  const { t } = useTranslation();

  if (kolonne.type === 'ja_nei') {
    return (
      <label htmlFor={id} className="flex min-h-[3rem] items-center gap-3">
        <input id={id} type="checkbox" checked={verdi === true} onChange={(e) => onEndre(e.target.checked)} className="h-5 w-5" />
        <span>{kolonne.tekst}</span>
      </label>
    );
  }

  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="mb-1 text-sm font-medium">
        {kolonne.tekst}
      </label>
      {kolonne.type === 'ett_valg' ? (
        <select id={id} value={typeof verdi === 'string' ? verdi : ''} onChange={(e) => onEndre(e.target.value)} className="felt">
          <option value="">{t('reise.tabell.velg')}</option>
          {kolonne.alternativer.map((a) => (
            <option key={a.verdi} value={a.verdi}>
              {a.tekst}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type="text"
          inputMode={kolonne.type === 'tall' ? 'decimal' : undefined}
          value={typeof verdi === 'string' ? verdi : ''}
          onChange={(e) => onEndre(e.target.value)}
          className="felt"
        />
      )}
    </div>
  );
}
